import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import { SideNav } from "@/components/SideNav";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, XCircle, Clock, IndianRupee, Calendar } from "lucide-react";
import { formatINR, formatNum } from "@/lib/format";

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

export default function AdminLateEntries() {
  const [tab, setTab] = useState("sales");
  const [sales, setSales] = useState([]);
  const [att, setAtt] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, a] = await Promise.all([
        api.get("/sales/late-entries"),
        api.get("/attendance/late-entries"),
      ]);
      setSales(s.data.filter(r => r.approval_status === "pending"));
      setAtt(a.data);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const approveSale = async (id) => {
    try {
      await api.patch(`/sales/${id}/late-approval`, { approval_status: "approved" });
      toast.success("Sale approved");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const approveAtt = async (id) => {
    try {
      await api.patch(`/attendance/${id}/approval`, { approval_status: "approved" });
      toast.success("Attendance approved");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const reject = async () => {
    if (!rejectFor) return;
    const { kind, id } = rejectFor;
    try {
      if (kind === "sale") {
        await api.patch(`/sales/${id}/late-approval`, { approval_status: "rejected", reason: rejectReason || "" });
      } else {
        await api.patch(`/attendance/${id}/approval`, { approval_status: "rejected", reason: rejectReason || "" });
      }
      toast.success("Rejected");
      setRejectFor(null); setRejectReason("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <AppHeader title="Late-entry Approvals" subtitle="Back-dated Sales & Attendance" />
      <div className="flex-1 flex">
        <SideNav />
        <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-6 py-6 space-y-4">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="bg-white border border-gray-200 rounded-xl p-1">
              <TabsTrigger value="sales" data-testid="tab-late-sales" className="data-[state=active]:bg-red-50 data-[state=active]:text-brand-red rounded-lg">
                <IndianRupee className="h-4 w-4 mr-1.5" /> Sales ({sales.length})
              </TabsTrigger>
              <TabsTrigger value="attendance" data-testid="tab-late-attendance" className="data-[state=active]:bg-red-50 data-[state=active]:text-brand-red rounded-lg">
                <Calendar className="h-4 w-4 mr-1.5" /> Attendance ({att.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="sales" className="mt-4">
              {loading ? <div className="p-8 text-center text-gray-500">Loading…</div>
                : sales.length === 0 ? (
                  <Card className="p-10 rounded-xl border-dashed text-center bg-white">
                    <CheckCircle2 className="h-8 w-8 mx-auto text-green-500" />
                    <div className="mt-2 text-sm text-gray-500">No pending back-dated sales.</div>
                  </Card>
                ) : (
                  <div className="space-y-3" data-testid="late-sales-list">
                    {sales.map((s) => (
                      <Card key={s.id} className="p-4 rounded-xl border-amber-200 bg-amber-50/40" data-testid={`late-sale-${s.id}`}>
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge className="bg-amber-100 text-amber-800 border border-amber-200">
                                <Clock className="h-3 w-3 mr-1" /> LATE ENTRY
                              </Badge>
                              <span className="font-semibold text-gray-900">{s.store_name}</span>
                              <span className="text-xs text-gray-500">for {s.date}</span>
                            </div>
                            <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                              <div><span className="text-gray-500">Cups:</span> <span className="font-semibold">{formatNum(s.glasses_sold)}</span></div>
                              <div><span className="text-gray-500">Total:</span> <span className="font-semibold">{formatINR(s.total_sales)}</span></div>
                              <div><span className="text-gray-500">Cash+UPI+Card:</span> <span className="font-semibold">{formatINR((s.cash_amount||0)+(s.upi_amount||0)+(s.card_amount||0))}</span></div>
                              <div><span className="text-gray-500">By:</span> <span className="font-semibold">{s.promoter_name}</span></div>
                            </div>
                            {s.late_reason && (
                              <div className="mt-2 text-xs text-gray-700"><span className="font-semibold">Reason:</span> {s.late_reason}</div>
                            )}
                            <div className="text-[11px] text-gray-400 mt-1">Submitted {fmtDate(s.submitted_at)}</div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <Button size="sm" onClick={() => approveSale(s.id)} className="bg-green-600 hover:bg-green-700 text-white"
                              data-testid={`approve-late-sale-${s.id}`}>
                              <CheckCircle2 className="h-4 w-4 mr-1" /> Approve
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setRejectFor({ kind: "sale", id: s.id })}
                              data-testid={`reject-late-sale-${s.id}`}>
                              <XCircle className="h-4 w-4 mr-1" /> Reject
                            </Button>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
            </TabsContent>

            <TabsContent value="attendance" className="mt-4">
              {loading ? <div className="p-8 text-center text-gray-500">Loading…</div>
                : att.length === 0 ? (
                  <Card className="p-10 rounded-xl border-dashed text-center bg-white">
                    <CheckCircle2 className="h-8 w-8 mx-auto text-green-500" />
                    <div className="mt-2 text-sm text-gray-500">No pending back-dated attendance.</div>
                  </Card>
                ) : (
                  <div className="space-y-3" data-testid="late-attendance-list">
                    {att.map((a) => (
                      <Card key={a.id} className="p-4 rounded-xl border-amber-200 bg-amber-50/40" data-testid={`late-att-${a.id}`}>
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge className="bg-amber-100 text-amber-800 border border-amber-200">
                                <Clock className="h-3 w-3 mr-1" /> LATE ENTRY
                              </Badge>
                              <span className="font-semibold text-gray-900">{a.store_name}</span>
                              <span className="text-xs text-gray-500">for {a.date}</span>
                            </div>
                            <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                              <div><span className="text-gray-500">Status:</span> <span className="font-semibold">{a.status}</span></div>
                              <div><span className="text-gray-500">Distance:</span> <span className="font-semibold">{a.distance_m}m</span></div>
                              <div><span className="text-gray-500">Geo:</span> <span className={a.out_of_geofence ? "text-red-700 font-semibold" : "text-green-700 font-semibold"}>{a.out_of_geofence ? "Out" : "In"}</span></div>
                              <div><span className="text-gray-500">By:</span> <span className="font-semibold">{a.promoter_name}</span></div>
                            </div>
                            {a.late_reason && (
                              <div className="mt-2 text-xs text-gray-700"><span className="font-semibold">Reason:</span> {a.late_reason}</div>
                            )}
                            <div className="text-[11px] text-gray-400 mt-1">Checked-in {fmtDate(a.check_in_time)}</div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <Button size="sm" onClick={() => approveAtt(a.id)} className="bg-green-600 hover:bg-green-700 text-white"
                              data-testid={`approve-late-att-${a.id}`}>
                              <CheckCircle2 className="h-4 w-4 mr-1" /> Approve
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setRejectFor({ kind: "attendance", id: a.id })}
                              data-testid={`reject-late-att-${a.id}`}>
                              <XCircle className="h-4 w-4 mr-1" /> Reject
                            </Button>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
            </TabsContent>
          </Tabs>
        </main>
      </div>

      <Dialog open={!!rejectFor} onOpenChange={(v) => !v && setRejectFor(null)}>
        <DialogContent data-testid="reject-dialog" className="rounded-2xl">
          <DialogHeader><DialogTitle>Reject this entry?</DialogTitle></DialogHeader>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Reason (optional)</label>
            <Textarea data-testid="reject-reason" rows={3} value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g. Reason unclear — please resubmit with details." />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectFor(null)}>Cancel</Button>
            <Button data-testid="confirm-reject-btn" onClick={reject} className="bg-red-600 hover:bg-red-700 text-white">
              <XCircle className="h-4 w-4 mr-1.5" /> Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
